// =============================================================================
// domain/customMetrics.ts — the fifteen metrics a custom widget can chart.
// -----------------------------------------------------------------------------
// Relocated from the old client's `CUSTOM_METRICS` — semantics, not code (D3).
// Each one takes a set of trades and returns a single number for that period.
//
// ── 🚨 NULL IS A REAL ANSWER, AND IT IS NOT ZERO ──
//
// `Win/Loss ratio` with no losers, and `Avg risk` where nothing carries a risk
// figure, both return **null** — there is nothing to divide. Coercing those to
// zero draws a line through the floor on every period the trader had a clean
// run, which reads as a collapse and is the opposite of what happened.
//
// ── ⚠ THE `directional` FLAG DECIDES WHETHER `L/S` IS OFFERED ──
//
// Only three metrics can honestly be split long versus short: Net P&L, Win rate
// and # Trades. The others either already describe one side (Avg win) or have no
// meaning per side in this model. The old client hides the toggle rather than
// disabling it, and a metric gaining the flag is a deliberate act — "it looked
// like it would work" is how a split that quietly means nothing gets shipped.
// =============================================================================

import { holdMinutes } from './excursionExit';

/** Everything a metric reads off a trade. */
export interface MetricTrade {
  netPnl: number | null;
  qty: number | null;
  fees: number | null;
  side: string | null;
  date: string | null;
  // ⚠ NOT optional. `effectiveTradeDate` takes `string | null`, and making
  // this optional here means every caller has to widen it back — the read
  // already supplies it on every row.
  sessionDate: string | null;
  timeIn: string | null;
  timeOut: string | null;
  rrRealized: number | null;
  riskAtEntry?: number | null | undefined;
}

/**
 * How a value is formatted, and it is not only cosmetic.
 *
 * ⚠ `$u` is UNSIGNED money — commissions and risk are costs, and rendering a
 * commission total as `-$240` when the trader paid $240 is a sign error dressed
 * as a convention.
 */
export type MetricUnit = '$' | '$u' | '%' | 'x' | '#' | 'R' | 'min';

export const CUSTOM_CATEGORIES = [
  'Profitability', 'Risk & Drawdown', 'Activity & Volume', 'Time',
] as const;
export type MetricCategory = (typeof CUSTOM_CATEGORIES)[number];

export interface CustomMetric {
  id: string;
  label: string;
  category: MetricCategory;
  unit: MetricUnit;
  /** Offers the L/S split. See the header — only three do. */
  directional?: boolean;
  compute: (ts: readonly MetricTrade[]) => number | null;
}

const pnl = (t: MetricTrade): number => Number(t.netPnl) || 0;
const sum = (ts: readonly MetricTrade[], f: (t: MetricTrade) => number): number =>
  ts.reduce((s, t) => s + f(t), 0);
const mean = (xs: readonly number[]): number | null =>
  (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

export const CUSTOM_METRICS: readonly CustomMetric[] = [
  {
    id: 'net_pnl', label: 'Net P&L', category: 'Profitability', unit: '$', directional: true,
    compute: (ts) => sum(ts, pnl),
  },
  {
    id: 'avg_pnl', label: 'Avg trade P&L', category: 'Profitability', unit: '$',
    compute: (ts) => (ts.length ? sum(ts, pnl) / ts.length : 0),
  },
  {
    id: 'win_rate', label: 'Win rate', category: 'Profitability', unit: '%', directional: true,
    // ⚠ SCRATCHES ARE EXCLUDED FROM BOTH SIDES — the denominator is wins+losses,
    // not the trade count. A flat day would otherwise drag the rate toward zero
    // while nothing was actually lost.
    compute: (ts) => {
      const w = ts.filter((t) => pnl(t) > 0).length;
      const l = ts.filter((t) => pnl(t) < 0).length;
      return (w + l) ? (w / (w + l)) * 100 : 0;
    },
  },
  {
    id: 'profit_factor', label: 'Profit factor', category: 'Profitability', unit: 'x',
    compute: (ts) => {
      let gp = 0; let gl = 0;
      for (const t of ts) { const p = pnl(t); if (p > 0) gp += p; else gl += -p; }
      return gl ? gp / gl : 0;
    },
  },
  {
    id: 'avg_win', label: 'Avg win', category: 'Profitability', unit: '$',
    compute: (ts) => mean(ts.filter((t) => pnl(t) > 0).map(pnl)) ?? 0,
  },
  {
    id: 'avg_loss', label: 'Avg loss', category: 'Profitability', unit: '$',
    compute: (ts) => mean(ts.filter((t) => pnl(t) < 0).map(pnl)) ?? 0,
  },
  {
    id: 'win_loss_ratio', label: 'Win/Loss ratio', category: 'Profitability', unit: 'x',
    // 🚨 NULL, NOT ZERO. No winners or no losers means there is no ratio — and a
    // zero here would draw a collapse on a period that had none.
    compute: (ts) => {
      const aw = mean(ts.filter((t) => pnl(t) > 0).map(pnl));
      const al = mean(ts.filter((t) => pnl(t) < 0).map(pnl));
      if (aw == null || al == null) return null;
      const abs = Math.abs(al);
      return abs ? aw / abs : null;
    },
  },
  {
    id: 'commissions', label: 'Commissions', category: 'Profitability', unit: '$u',
    compute: (ts) => sum(ts, (t) => Number(t.fees) || 0),
  },
  {
    id: 'avg_realized_r', label: 'Avg realized R', category: 'Risk & Drawdown', unit: 'R',
    compute: (ts) => mean(
      ts.map((t) => t.rrRealized).filter((v): v is number => v != null && Number.isFinite(v)),
    ) ?? 0,
  },
  {
    id: 'avg_risk', label: 'Avg risk ($)', category: 'Risk & Drawdown', unit: '$u',
    // 🚨 NULL when nothing carries a risk figure — see `win_loss_ratio`.
    compute: (ts) => mean(
      ts.map((t) => t.riskAtEntry)
        .filter((v): v is number => v != null && Number.isFinite(v) && v > 0),
    ),
  },
  {
    id: 'max_drawdown', label: 'Max drawdown', category: 'Risk & Drawdown', unit: '$',
    /**
     * ⚠ THE TRADES ARE RE-SORTED HERE, and they have to be. Drawdown is a
     * peak-to-trough walk, so it is the only metric whose answer depends on the
     * ORDER of the set it is given — and the set arriving grouped by period says
     * nothing about the order within one.
     *
     * ⚠ Returned NEGATIVE, matching the old client, so it plots below the axis
     * like the loss it is.
     */
    compute: (ts) => {
      const sorted = ts.slice().sort((a, b) => (
        `${a.date ?? ''}${a.timeIn ?? ''}`.localeCompare(`${b.date ?? ''}${b.timeIn ?? ''}`)
      ));
      let cum = 0; let peak = 0; let dd = 0;
      for (const t of sorted) {
        cum += pnl(t);
        if (cum > peak) peak = cum;
        if (peak - cum > dd) dd = peak - cum;
      }
      return -dd;
    },
  },
  {
    id: 'trades', label: '# Trades', category: 'Activity & Volume', unit: '#', directional: true,
    compute: (ts) => ts.length,
  },
  {
    id: 'volume', label: 'Contracts traded', category: 'Activity & Volume', unit: '#',
    compute: (ts) => sum(ts, (t) => Number(t.qty) || 0),
  },
  {
    id: 'avg_size', label: 'Avg position size', category: 'Activity & Volume', unit: '#',
    compute: (ts) => mean(
      ts.map((t) => t.qty).filter((v): v is number => v != null && Number.isFinite(v)),
    ) ?? 0,
  },
  {
    id: 'hold_time', label: 'Avg hold time', category: 'Time', unit: 'min',
    compute: (ts) => mean(
      ts.map((t) => holdMinutes(t)).filter((v): v is number => v != null),
    ) ?? 0,
  },
];

/**
 * ⚠ FALLS BACK TO THE FIRST METRIC rather than returning undefined, matching the
 * old client. A widget stored with a metric id that has since been renamed still
 * renders something instead of taking the tab down — and the id is user data in
 * `preferences`, so it CAN go stale.
 */
export function metricById(id: string | null | undefined): CustomMetric {
  return CUSTOM_METRICS.find((m) => m.id === id) ?? (CUSTOM_METRICS[0] as CustomMetric);
}

/** The metrics of one category, in catalogue order. For the `<optgroup>`s. */
export function metricsInCategory(cat: MetricCategory): CustomMetric[] {
  return CUSTOM_METRICS.filter((m) => m.category === cat);
}
